import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import {
  FaUserPlus,
  FaEnvelope,
  FaTrash,
  FaCrown,
  FaUser,
  FaCopy,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaWhatsapp,
  FaSms,
  FaLink,
  FaUsers,
  FaQuestionCircle,
  FaShieldAlt,
  FaBell,
} from "react-icons/fa";
import api from "../../api/axiosClient";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = [
  {
    value: "helper",
    label: "Helper",
    color: "#000000",
    desc: "Assists on jobs, no management access",
  },
  {
    value: "foreman",
    label: "Foreman",
    color: "#7a7000",
    desc: "Manages job tasks and progress updates",
  },
  {
    value: "partner",
    label: "Partner",
    color: "#000000",
    desc: "Full access to jobs and quotes",
  },
];

const STATUS_CFG = {
  pending_invite: {
    bg: "#fff000",
    color: "#000000",
    icon: <FaClock />,
    label: "Invite Sent",
  },
  pending_approval: {
    bg: "#fff000",
    color: "#000000",
    icon: <FaQuestionCircle />,
    label: "Awaiting Approval",
  },
  accepted: {
    bg: "#000000",
    color: "#fff000",
    icon: <FaCheckCircle />,
    label: "Active",
  },
  rejected: {
    bg: "#fee2e2",
    color: "#991b1b",
    icon: <FaTimesCircle />,
    label: "Rejected",
  },
  declined: {
    bg: "#fee2e2",
    color: "#991b1b",
    icon: <FaTimesCircle />,
    label: "Declined",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending_invite;

  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "4px 12px",
        borderRadius: "50px",
        fontSize: ".78rem",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function MembersTab({ craftsman, addToast }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteMethod, setInviteMethod] = useState("email");
  const [inviteRole, setInviteRole] = useState("helper");
  const [inviteValue, setInviteValue] = useState("");
  const [inviteName, setInviteName] = useState("");

  const [sending, setSending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const inviteLink = `https://kaakazini.com/join?craftsman=${craftsman?.id}&role=${inviteRole}`;

  const pendingApprovalCount = approvalQueue.length;

  useEffect(() => {
    fetchAll();
  }, []);

  // ─── Fetch Data ────────────────────────────────────────────────────────────

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [mRes, iRes, aRes] = await Promise.all([
        api.get("/craftsman/members/"),
        api.get("/craftsman/invites/"),
        api.get("/craftsman/members/pending-approval/"),
      ]);

      setMembers(mRes.data || []);
      setInvites(iRes.data || []);
      setApprovalQueue(aRes.data || []);
    } catch (err) {
      console.warn("Members API not ready yet:", err.message);

      setMembers([]);
      setInvites([]);
      setApprovalQueue([]);
    } finally {
      setLoading(false);
    }
  };

  // ─── Approve Member ────────────────────────────────────────────────────────

  const handleApprove = async (applicantId) => {
    setApprovingId(applicantId);

    try {
      await api.post(`/craftsman/members/${applicantId}/approve/`);

      const approved = approvalQueue.find(
        (a) => a.id === applicantId
      );

      if (approved) {
        setApprovalQueue((prev) =>
          prev.filter((a) => a.id !== applicantId)
        );

        setMembers((prev) => [
          ...prev,
          {
            ...approved,
            status: "accepted",
          },
        ]);
      }

      addToast?.(
        `${approved?.full_name || "Member"} has been approved and added to your team!`,
        "success",
        "Member Approved"
      );
    } catch {
      addToast?.(
        "Failed to approve member. Please try again.",
        "error",
        "Error"
      );
    } finally {
      setApprovingId(null);
    }
  };

  // ─── Reject Member ─────────────────────────────────────────────────────────

  const handleReject = async (applicantId) => {
    if (!window.confirm("Reject this member request?")) return;

    setApprovingId(applicantId);

    try {
      await api.post(`/craftsman/members/${applicantId}/reject/`);

      setApprovalQueue((prev) =>
        prev.filter((a) => a.id !== applicantId)
      );

      addToast?.(
        "Member request rejected.",
        "warning",
        "Request Rejected"
      );
    } catch {
      addToast?.(
        "Failed to reject. Please try again.",
        "error",
        "Error"
      );
    } finally {
      setApprovingId(null);
    }
  };

  // ─── Send Invite ───────────────────────────────────────────────────────────

  const handleSendInvite = async () => {
    if (inviteMethod !== "link" && !inviteValue.trim()) {
      addToast?.(
        "Please enter a valid email or phone number.",
        "warning",
        "Missing Info"
      );
      return;
    }

    setSending(true);

    try {
      await api.post("/craftsman/invites/", {
        method: inviteMethod,
        contact: inviteValue,
        name: inviteName,
        role: inviteRole,
      });

      addToast?.(
        "Invite sent! They'll appear here once they accept and you approve them.",
        "success",
        "Invite Sent!"
      );

      setInviteValue("");
      setInviteName("");
      setShowInvite(false);

      fetchAll();
    } catch (err) {
      addToast?.(
        err.response?.data?.detail || "Failed to send invite.",
        "error",
        "Send Failed"
      );
    } finally {
      setSending(false);
    }
  };

  // ─── Copy Invite Link ──────────────────────────────────────────────────────

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);

    setLinkCopied(true);

    setTimeout(() => {
      setLinkCopied(false);
    }, 2500);
  };

  // ─── WhatsApp Invite ───────────────────────────────────────────────────────

  const handleWhatsAppInvite = () => {
    const text = encodeURIComponent(
      `Hi ${inviteName || "there"}! 👷\n\n` +
        `I'd like you to join my team on *Kaakazini* as a *${inviteRole}*.\n\n` +
        `Click the link to request to join — I'll approve you right away:\n${inviteLink}`
    );

    window.open(
      `https://wa.me/?text=${text}`,
      "_blank"
    );
  };

  // ─── Remove Member ─────────────────────────────────────────────────────────

  const handleRemoveMember = async (id, name) => {
    if (
      !window.confirm(
        `Remove ${name || "this member"} from your team?`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/craftsman/members/${id}/`);

      setMembers((prev) =>
        prev.filter((m) => m.id !== id)
      );

      addToast?.(
        "Member removed from your team.",
        "info",
        "Member Removed"
      );
    } catch {
      addToast?.(
        "Failed to remove member.",
        "error",
        "Error"
      );
    }
  };

  // ─── Revoke Invite ─────────────────────────────────────────────────────────

  const handleRevokeInvite = async (id) => {
    try {
      await api.delete(`/craftsman/invites/${id}/`);

      setInvites((prev) =>
        prev.filter((i) => i.id !== id)
      );

      addToast?.(
        "Invite revoked.",
        "info",
        "Revoked"
      );
    } catch {
      addToast?.(
        "Failed to revoke invite.",
        "error",
        "Error"
      );
    }
  };

  // ─── Derived Data ──────────────────────────────────────────────────────────

  const activeMembers = members.filter(
    (m) => m.status === "accepted"
  );

  const pendingInvites = invites.filter(
    (i) => i.status === "pending_invite"
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`

        /* ============================================================
           KAAKAZINI BRAND
           Font: Raleway
           Yellow: #fff000
           Black: #000000
           White: #ffffff
        ============================================================ */

        .members-wrap {
          font-family: "Raleway", sans-serif;
          color: #000000;
        }

        .members-wrap *,
        .members-wrap button,
        .members-wrap input {
          font-family: "Raleway", sans-serif;
        }

        /* ============================================================
           Stats
        ============================================================ */

        .stat-chip {
          background: #ffffff;
          border: 2px solid #000000;
          border-radius: 14px;
          padding: 1rem 1.25rem;
          flex: 1;
          min-width: 120px;
          text-align: center;
          transition: all .2s ease;
        }

        .stat-chip:hover {
          background: #fff000;
          transform: translateY(-2px);
        }

        .stat-chip.alert-chip {
          background: #fff000;
          border-color: #000000;
        }

        .stat-chip .sv {
          font-size: 1.875rem;
          font-weight: 800;
          color: #000000;
          line-height: 1;
        }

        .stat-chip.alert-chip .sv {
          color: #000000;
        }

        .stat-chip .sl {
          font-size: .75rem;
          color: #000000;
          font-weight: 600;
          margin-top: 4px;
        }

        /* ============================================================
           Approval Banner
        ============================================================ */

        .approval-banner {
          background: #ffffff;
          border: 2px solid #000000;
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          margin-bottom: 1.5rem;
          position: relative;
          overflow: hidden;
        }

        .approval-banner::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #fff000;
        }

        .approval-banner h6 {
          color: #000000;
          font-weight: 800;
          margin-bottom: .25rem;
        }

        .approval-banner p {
          color: #000000;
          font-size: .875rem;
          margin: 0;
        }

        /* ============================================================
           Approval Cards
        ============================================================ */

        .approval-card {
          background: #ffffff;
          border: 2px solid #000000;
          border-radius: 14px;
          padding: 1rem 1.25rem;
          margin-bottom: .75rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          animation: slideIn .3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .approval-card .avi {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: #fff000;
          color: #000000;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          flex-shrink: 0;
        }

        /* ============================================================
           Approve Button
        ============================================================ */

        .approve-btn {
          background: #fff000;
          border: 2px solid #000000;
          border-radius: 10px;
          color: #000000;
          font-weight: 700;
          padding: .5rem 1.1rem;
          font-size: .875rem;
          cursor: pointer;
          transition: all .2s;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .approve-btn:hover {
          background: #000000;
          color: #fff000;
          transform: translateY(-1px);
        }

        .approve-btn:disabled {
          opacity: .6;
          cursor: not-allowed;
          transform: none;
        }

        /* ============================================================
           Reject Button
        ============================================================ */

        .reject-btn {
          background: #ffffff;
          border: 2px solid #ef4444;
          color: #ef4444;
          border-radius: 10px;
          font-weight: 700;
          padding: .5rem 1.1rem;
          font-size: .875rem;
          cursor: pointer;
          transition: all .2s;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .reject-btn:hover {
          background: #fee2e2;
          border-color: #dc2626;
          color: #dc2626;
        }

        .reject-btn:disabled {
          opacity: .6;
          cursor: not-allowed;
        }

        /* ============================================================
           Role Cards
        ============================================================ */

        .role-card {
          border: 2px solid #000000;
          border-radius: 14px;
          padding: .875rem;
          cursor: pointer;
          transition: all .2s;
          flex: 1;
          background: #ffffff;
        }

        .role-card:hover,
        .role-card.sel {
          border-color: #000000;
          background: #fff000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, .12);
        }

        /* ============================================================
           Method Tabs
        ============================================================ */

        .m-tab {
          flex: 1;
          padding: .65rem;
          border: 2px solid #000000;
          border-radius: 12px;
          background: #ffffff;
          cursor: pointer;
          font-weight: 600;
          font-size: .8rem;
          transition: all .2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          color: #000000;
        }

        .m-tab:hover,
        .m-tab.act {
          border-color: #000000;
          color: #000000;
          background: #fff000;
        }

        /* ============================================================
           Member Rows
        ============================================================ */

        .member-row {
          background: #ffffff;
          border: 2px solid #000000;
          border-radius: 14px;
          padding: 1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all .25s;
          margin-bottom: .75rem;
        }

        .member-row:hover {
          background: #fff000;
          border-color: #000000;
          box-shadow: 0 4px 16px rgba(0, 0, 0, .08);
        }

        .member-row .avi {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          flex-shrink: 0;
        }

        /* ============================================================
           Invite CTA
        ============================================================ */

        .invite-cta {
          background: #fff000;
          border: 2px solid #000000;
          border-radius: 12px;
          color: #000000;
          font-weight: 700;
          padding: .875rem 2rem;
          font-size: 1rem;
          transition: all .2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .invite-cta:hover {
          background: #000000;
          color: #fff000;
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(0, 0, 0, .15);
        }

        .invite-cta:disabled {
          opacity: .7;
          transform: none;
          cursor: not-allowed;
        }

        /* ============================================================
           Link Box
        ============================================================ */

        .link-box {
          background: #ffffff;
          border: 2px dashed #000000;
          border-radius: 12px;
          padding: 1rem;
          font-family: monospace !important;
          font-size: .78rem;
          color: #000000;
          word-break: break-all;
        }

        /* ============================================================
           Inputs
        ============================================================ */

        .mi {
          border: 2px solid #000000;
          border-radius: 10px;
          padding: .7rem 1rem;
          width: 100%;
          font-size: .9375rem;
          transition: all .2s;
          color: #000000;
          background: #ffffff;
        }

        .mi::placeholder {
          color: #777777;
        }

        .mi:focus {
          outline: none;
          border-color: #000000;
          box-shadow: 0 0 0 4px rgba(255, 240, 0, .35);
        }

        /* ============================================================
           Empty State
        ============================================================ */

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: #ffffff;
          border-radius: 20px;
          border: 2px dashed #000000;
        }

        /* ============================================================
           Flow Diagram
        ============================================================ */

        .flow-diagram {
          display: flex;
          align-items: center;
          gap: .5rem;
          flex-wrap: wrap;
          margin-top: .75rem;
        }

        .flow-step {
          background: #ffffff;
          border: 2px solid #000000;
          border-radius: 8px;
          padding: .35rem .75rem;
          font-size: .75rem;
          font-weight: 600;
          color: #000000;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .flow-arrow {
          color: #000000;
          font-size: .875rem;
          font-weight: 700;
        }

        /* ============================================================
           Section Labels
        ============================================================ */

        .section-label {
          font-size: .72rem;
          font-weight: 700;
          color: #000000;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: .75rem;
        }

        /* ============================================================
           Notification Dot
        ============================================================ */

        .notification-dot {
          width: 8px;
          height: 8px;
          background: #fff000;
          border: 1px solid #000000;
          border-radius: 50%;
          display: inline-block;
          margin-left: 5px;
          animation: pulse-dot 1.5s ease-in-out infinite;
        }

        @keyframes pulse-dot {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }

          50% {
            opacity: .6;
            transform: scale(1.3);
          }
        }

        /* ============================================================
           Modal Styling
        ============================================================ */

        .members-wrap .modal-content {
          border: 2px solid #000000;
          border-radius: 18px;
          overflow: hidden;
        }

        .members-wrap .modal-header {
          background: #ffffff;
        }

        .members-wrap .modal-title {
          color: #000000;
        }

        .members-wrap .modal-body {
          color: #000000;
        }

        /* ============================================================
           Responsive
        ============================================================ */

        @media (max-width: 768px) {
          .approval-card {
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .approval-card > .flex-grow-1 {
            min-width: calc(100% - 60px);
          }

          .approval-card > .d-flex {
            width: 100%;
          }

          .member-row {
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .member-row > .flex-grow-1 {
            min-width: calc(100% - 60px);
          }

          .member-row > .d-flex {
            width: 100%;
            margin-left: 60px;
          }

          .invite-cta {
            width: 100%;
            justify-content: center;
          }

          .flow-arrow {
            display: none;
          }

          .flow-step {
            flex: 1;
            min-width: 130px;
            justify-content: center;
          }
        }

        @media (max-width: 576px) {
          .stat-chip {
            min-width: calc(50% - .5rem);
          }

          .approval-card,
          .member-row {
            padding: .875rem;
          }

          .member-row > .d-flex {
            margin-left: 0;
          }

          .role-card {
            min-width: 100%;
          }

          .m-tab {
            padding: .65rem .4rem;
          }
        }

      `}</style>

      <div className="members-wrap">

        {/* ============================================================
            Approval Flow Banner
        ============================================================ */}

        <div className="approval-banner">

          <h6 className="d-flex align-items-center gap-2">
            <FaShieldAlt />
            How Team Approval Works
          </h6>

          <div className="flow-diagram">

            <div className="flow-step">
              <FaEnvelope size={11} />
              You invite
            </div>

            <span className="flow-arrow">→</span>

            <div className="flow-step">
              <FaUser size={11} />
              They accept link
            </div>

            <span className="flow-arrow">→</span>

            <div
              className="flow-step"
              style={{
                background: "#fff000",
                borderColor: "#000000",
                color: "#000000",
              }}
            >
              <FaShieldAlt size={11} />
              You approve
            </div>

            <span className="flow-arrow">→</span>

            <div
              className="flow-step"
              style={{
                background: "#000000",
                borderColor: "#000000",
                color: "#fff000",
              }}
            >
              <FaCheckCircle size={11} />
              Active member
            </div>

          </div>

          <p
            className="mt-2"
            style={{
              fontSize: ".8rem",
              color: "#000000",
            }}
          >
            <strong>You are in control.</strong>{" "}
            Nobody joins your team without your explicit approval.
          </p>

        </div>

        {/* ============================================================
            Stats
        ============================================================ */}

        <div className="d-flex gap-3 flex-wrap mb-4">

          <div className="stat-chip">
            <div className="sv">
              {activeMembers.length}
            </div>

            <div className="sl">
              Active Members
            </div>
          </div>

          <div
            className={`stat-chip ${
              pendingApprovalCount > 0
                ? "alert-chip"
                : ""
            }`}
          >
            <div className="sv">
              {pendingApprovalCount}
            </div>

            <div className="sl">
              Need Your Approval

              {pendingApprovalCount > 0 && (
                <span className="notification-dot ms-1" />
              )}
            </div>
          </div>

          <div className="stat-chip">
            <div className="sv">
              {pendingInvites.length}
            </div>

            <div className="sl">
              Awaiting Response
            </div>
          </div>

        </div>

        {/* ============================================================
            Header
        ============================================================ */}

        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">

          <div>

            <h5 className="fw-bold mb-0">
              Team Members

              {pendingApprovalCount > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    background: "#fff000",
                    color: "#000000",
                    border: "1px solid #000000",
                    borderRadius: "50px",
                    padding: "2px 10px",
                    fontSize: ".75rem",
                    fontWeight: 800,
                  }}
                >
                  {pendingApprovalCount} need approval
                </span>
              )}
            </h5>

            <small className="text-muted">
              Invite and approve your craftsman team
            </small>

          </div>

          <button
            className="invite-cta"
            onClick={() => setShowInvite(true)}
          >
            <FaUserPlus />
            Invite Member
          </button>

        </div>

        {/* ============================================================
            Loading / Content
        ============================================================ */}

        {loading ? (

          <div className="text-center py-5">

            <div
              className="spinner-border"
              style={{
                color: "#000000",
              }}
            />

            <p className="mt-3 text-muted">
              Loading team...
            </p>

          </div>

        ) : (

          <>

            {/* ========================================================
                Pending Approval
            ======================================================== */}

            {approvalQueue.length > 0 && (
              <>

                <div
                  className="section-label d-flex align-items-center gap-2"
                  style={{
                    color: "#000000",
                  }}
                >
                  <FaBell size={11} />

                  Waiting for your approval (
                  {approvalQueue.length}
                  )
                </div>

                {approvalQueue.map((applicant) => {

                  const role =
                    ROLES.find(
                      (r) => r.value === applicant.role
                    ) || ROLES[0];

                  const initials = (
                    applicant.full_name || "?"
                  )
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);

                  const isProcessing =
                    approvingId === applicant.id;

                  return (

                    <div
                      key={applicant.id}
                      className="approval-card"
                    >

                      <div className="avi">
                        {initials}
                      </div>

                      <div className="flex-grow-1">

                        <div
                          className="fw-bold"
                          style={{
                            fontSize: ".9375rem",
                          }}
                        >
                          {applicant.full_name}
                        </div>

                        <div
                          className="text-muted"
                          style={{
                            fontSize: ".8rem",
                          }}
                        >
                          {applicant.email ||
                            applicant.phone}

                          {" · "}

                          <span
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              background: "#fff000",
                              padding: "2px 6px",
                              borderRadius: 5,
                            }}
                          >
                            Requested: {role.label}
                          </span>
                        </div>

                        <div
                          style={{
                            fontSize: ".75rem",
                            color: "#777777",
                            marginTop: 4,
                          }}
                        >
                          Joined via invite link ·{" "}
                          {applicant.joined_at ||
                            "Just now"}
                        </div>

                      </div>

                      <div className="d-flex gap-2 flex-wrap">

                        <button
                          className="approve-btn"
                          onClick={() =>
                            handleApprove(
                              applicant.id
                            )
                          }
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            "..."
                          ) : (
                            <>
                              <FaCheckCircle />
                              Approve
                            </>
                          )}
                        </button>

                        <button
                          className="reject-btn"
                          onClick={() =>
                            handleReject(
                              applicant.id
                            )
                          }
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            "..."
                          ) : (
                            <>
                              <FaTimesCircle />
                              Reject
                            </>
                          )}
                        </button>

                      </div>

                    </div>
                  );
                })}

              </>
            )}

            {/* ========================================================
                Active Members
            ======================================================== */}

            {activeMembers.length > 0 && (
              <>

                <div className="section-label mt-3">
                  Active Team ({activeMembers.length})
                </div>

                {activeMembers.map((m) => {

                  const role =
                    ROLES.find(
                      (r) => r.value === m.role
                    ) || ROLES[0];

                  const initials = (
                    m.full_name || "?"
                  )
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);

                  return (

                    <div
                      key={m.id}
                      className="member-row"
                    >

                      <div
                        className="avi"
                        style={{
                          background:
                            role.value === "foreman"
                              ? "#fff000"
                              : "#000000",
                          color:
                            role.value === "foreman"
                              ? "#000000"
                              : "#fff000",
                        }}
                      >
                        {initials}
                      </div>

                      <div className="flex-grow-1">

                        <div
                          className="fw-bold"
                          style={{
                            fontSize: ".9375rem",
                          }}
                        >
                          {m.full_name}
                        </div>

                        <div
                          className="text-muted"
                          style={{
                            fontSize: ".8rem",
                          }}
                        >
                          {m.email || m.phone}
                        </div>

                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap">

                        <span
                          style={{
                            background:
                              role.value ===
                              "foreman"
                                ? "#fff000"
                                : "#000000",
                            color:
                              role.value ===
                              "foreman"
                                ? "#000000"
                                : "#fff000",
                            borderRadius: 8,
                            padding: "3px 10px",
                            fontSize: ".78rem",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {m.role === "partner" ? (
                            <FaCrown size={11} />
                          ) : (
                            <FaUser size={10} />
                          )}

                          {role.label}
                        </span>

                        <span
                          style={{
                            background: "#fff000",
                            color: "#000000",
                            border: "1px solid #000000",
                            padding: "4px 12px",
                            borderRadius: "50px",
                            fontSize: ".78rem",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <FaCheckCircle />
                          Active
                        </span>

                        <button
                          style={{
                            border:
                              "1.5px solid #ef4444",
                            color: "#ef4444",
                            borderRadius: 8,
                            padding: "4px 10px",
                            background: "#ffffff",
                            cursor: "pointer",
                            fontSize: ".8rem",
                          }}
                          onClick={() =>
                            handleRemoveMember(
                              m.id,
                              m.full_name
                            )
                          }
                          title="Remove member"
                        >
                          <FaTrash size={11} />
                        </button>

                      </div>

                    </div>
                  );
                })}

              </>
            )}

            {/* ========================================================
                Pending Invites
            ======================================================== */}

            {pendingInvites.length > 0 && (
              <>

                <div className="section-label mt-3">
                  Sent — Awaiting Response (
                  {pendingInvites.length}
                  )
                </div>

                {pendingInvites.map((inv) => {

                  const role =
                    ROLES.find(
                      (r) => r.value === inv.role
                    ) || ROLES[0];

                  return (

                    <div
                      key={inv.id}
                      className="member-row"
                      style={{
                        opacity: .85,
                      }}
                    >

                      <div
                        className="avi"
                        style={{
                          background: "#fff000",
                          color: "#000000",
                        }}
                      >
                        <FaEnvelope />
                      </div>

                      <div className="flex-grow-1">

                        <div
                          className="fw-bold"
                          style={{
                            fontSize: ".9375rem",
                          }}
                        >
                          {inv.name ||
                            inv.contact}
                        </div>

                        <div
                          className="text-muted"
                          style={{
                            fontSize: ".8rem",
                          }}
                        >
                          {inv.contact} · Invite sent
                        </div>

                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap">

                        <span
                          style={{
                            background:
                              role.value === "foreman"
                                ? "#fff000"
                                : "#000000",
                            color:
                              role.value === "foreman"
                                ? "#000000"
                                : "#fff000",
                            borderRadius: 8,
                            padding: "3px 10px",
                            fontSize: ".78rem",
                            fontWeight: 700,
                          }}
                        >
                          {role.label}
                        </span>

                        <span
                          style={{
                            background: "#fff000",
                            color: "#000000",
                            border:
                              "1px solid #000000",
                            padding: "4px 12px",
                            borderRadius: "50px",
                            fontSize: ".78rem",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <FaClock />
                          Invite Sent
                        </span>

                        <button
                          style={{
                            border:
                              "1.5px solid #000000",
                            color: "#000000",
                            borderRadius: 8,
                            padding: "4px 10px",
                            background: "#ffffff",
                            cursor: "pointer",
                            fontSize: ".75rem",
                            fontWeight: 600,
                          }}
                          onClick={() =>
                            handleRevokeInvite(
                              inv.id
                            )
                          }
                        >
                          Revoke
                        </button>

                      </div>

                    </div>
                  );
                })}

              </>
            )}

            {/* ========================================================
                Empty State
            ======================================================== */}

            {activeMembers.length === 0 &&
              approvalQueue.length === 0 &&
              pendingInvites.length === 0 && (

                <div className="empty-state">

                  <FaUsers
                    size={56}
                    color="#000000"
                    style={{
                      opacity: .15,
                      display: "block",
                      margin: "0 auto 1rem",
                    }}
                  />

                  <h5
                    className="fw-bold"
                    style={{
                      color: "#000000",
                    }}
                  >
                    No team members yet
                  </h5>

                  <p className="text-muted">
                    Invite helpers, foremen, or
                    partners. You approve everyone
                    before they join.
                  </p>

                  <button
                    className="invite-cta mt-2"
                    onClick={() =>
                      setShowInvite(true)
                    }
                  >
                    <FaUserPlus />
                    Invite First Member
                  </button>

                </div>
              )}

          </>
        )}

        {/* ============================================================
            INVITE MODAL
        ============================================================ */}

        <Modal
          show={showInvite}
          onHide={() => setShowInvite(false)}
          centered
        >

          <Modal.Header
            closeButton
            className="border-0 pb-0"
          >

            <Modal.Title
              style={{
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#000000",
              }}
            >
              <FaUserPlus />
              Invite a Team Member
            </Modal.Title>

          </Modal.Header>

          <Modal.Body className="pt-2">

            {/* ======================================================
                Role
            ====================================================== */}

            <p
              style={{
                fontSize: ".875rem",
                fontWeight: 700,
                color: "#000000",
                marginBottom: 8,
              }}
            >
              1. Choose their role
            </p>

            <div className="d-flex gap-2 mb-4 flex-wrap">

              {ROLES.map((role) => (

                <div
                  key={role.value}
                  className={`role-card ${
                    inviteRole === role.value
                      ? "sel"
                      : ""
                  }`}
                  onClick={() =>
                    setInviteRole(role.value)
                  }
                >

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: ".875rem",
                      marginBottom: 4,
                    }}
                  >

                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background:
                          role.value === "foreman"
                            ? "#fff000"
                            : "#000000",
                        border:
                          "1px solid #000000",
                        display: "inline-block",
                        marginRight: 6,
                      }}
                    />

                    {role.label}

                  </div>

                  <div
                    style={{
                      fontSize: ".75rem",
                      color: "#555555",
                    }}
                  >
                    {role.desc}
                  </div>

                </div>
              ))}

            </div>

            {/* ======================================================
                Method
            ====================================================== */}

            <p
              style={{
                fontSize: ".875rem",
                fontWeight: 700,
                color: "#000000",
                marginBottom: 8,
              }}
            >
              2. How to send the invite
            </p>

            <div className="d-flex gap-2 mb-4">

              {[
                {
                  id: "email",
                  icon: <FaEnvelope />,
                  label: "Email",
                },
                {
                  id: "sms",
                  icon: <FaSms />,
                  label: "SMS",
                },

                // WhatsApp and Link remain
                // intentionally disabled as in
                // the original implementation.

                // {
                //   id: "whatsapp",
                //   icon: <FaWhatsapp />,
                //   label: "WhatsApp",
                // },

                // {
                //   id: "link",
                //   icon: <FaLink />,
                //   label: "Link",
                // },
              ].map((m) => (

                <button
                  key={m.id}
                  className={`m-tab ${
                    inviteMethod === m.id
                      ? "act"
                      : ""
                  }`}
                  onClick={() =>
                    setInviteMethod(m.id)
                  }
                >
                  {m.icon}

                  <span className="d-none d-sm-inline">
                    {m.label}
                  </span>
                </button>

              ))}

            </div>

            {/* ======================================================
                Details
            ====================================================== */}

            {inviteMethod !== "link" ? (

              <>

                <p
                  style={{
                    fontSize: ".875rem",
                    fontWeight: 700,
                    color: "#000000",
                    marginBottom: 8,
                  }}
                >
                  3. Their contact details
                </p>

                <input
                  className="mi mb-3"
                  placeholder="Name (optional)"
                  value={inviteName}
                  onChange={(e) =>
                    setInviteName(e.target.value)
                  }
                />

                <input
                  className="mi mb-3"
                  placeholder={
                    inviteMethod === "email"
                      ? "Email address"
                      : "Phone e.g. 0712345678"
                  }
                  type={
                    inviteMethod === "email"
                      ? "email"
                      : "tel"
                  }
                  value={inviteValue}
                  onChange={(e) =>
                    setInviteValue(e.target.value)
                  }
                />

                {inviteMethod === "whatsapp" && (

                  <div
                    style={{
                      background: "#fff000",
                      border:
                        "2px solid #000000",
                      borderRadius: 10,
                      padding: ".75rem 1rem",
                      color: "#000000",
                      fontSize: ".875rem",
                    }}
                  >
                    <FaWhatsapp className="me-2" />

                    Opens WhatsApp with a
                    pre-filled invite message
                    including your approval notice.
                  </div>

                )}

              </>

            ) : (

              <>

                <p
                  style={{
                    fontSize: ".875rem",
                    fontWeight: 700,
                    color: "#000000",
                    marginBottom: 8,
                  }}
                >
                  3. Share this invite link
                </p>

                <div className="link-box mb-3">
                  {inviteLink}
                </div>

                <div
                  style={{
                    background: "#fff000",
                    border:
                      "2px solid #000000",
                    borderRadius: 10,
                    padding: ".75rem 1rem",
                    color: "#000000",
                    fontSize: ".8rem",
                    marginBottom: "1rem",
                  }}
                >
                  <FaShieldAlt className="me-2" />

                  Anyone who clicks this link
                  must still be{" "}
                  <strong>
                    approved by you
                  </strong>{" "}
                  before joining.
                </div>

                <button
                  onClick={handleCopyLink}
                  style={{
                    width: "100%",
                    padding: ".75rem",
                    borderRadius: 12,
                    fontWeight: 700,
                    background: linkCopied
                      ? "#fff000"
                      : "#000000",
                    color: linkCopied
                      ? "#000000"
                      : "#fff000",
                    border:
                      "2px solid #000000",
                    cursor: "pointer",
                    transition: "all .3s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {linkCopied ? (
                    <>
                      <FaCheckCircle />
                      Copied!
                    </>
                  ) : (
                    <>
                      <FaCopy />
                      Copy Invite Link
                    </>
                  )}
                </button>

              </>

            )}

          </Modal.Body>

          {/* ========================================================
              Modal Footer
          ======================================================== */}

          <Modal.Footer
            className="border-0 pt-0"
          >

            <button
              onClick={() =>
                setShowInvite(false)
              }
              style={{
                border:
                  "2px solid #000000",
                borderRadius: 12,
                fontWeight: 600,
                padding: ".7rem 1.5rem",
                background: "#ffffff",
                color: "#000000",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>

            {inviteMethod !== "link" && (

              inviteMethod === "whatsapp" ? (

                <button
                  className="invite-cta"
                  onClick={
                    handleWhatsAppInvite
                  }
                >
                  <FaWhatsapp />
                  Open WhatsApp
                </button>

              ) : (

                <button
                  className="invite-cta"
                  onClick={handleSendInvite}
                  disabled={sending}
                >
                  {sending ? (
                    "Sending..."
                  ) : (
                    <>
                      <FaUserPlus />
                      Send Invite
                    </>
                  )}
                </button>

              )

            )}

          </Modal.Footer>

        </Modal>

      </div>
    </>
  );
}

MembersTab.propTypes = {
  craftsman: PropTypes.object,
  addToast: PropTypes.func,
};

export default MembersTab;